import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Button, Modal, Table } from '@/shared/ui';
import type { Column } from '@/shared/ui/Table';
import patterns from '@/shared/ui/styles/patterns.module.css';
import { useAllPayers } from '../hooks/useAllPayers';
import { statusBadge, strategyBadge } from '../components/payerBadges';
import {
  PayerDirectoryFields,
  type PayerDirectoryFieldValues,
} from '../components/PayerDirectoryFields';
import { DEFAULT_PAYER_DIRECTORY_FIELD_VALUES } from '../components/payerDirectoryDefaults';
import type { DirectoryPayer, DirectoryPayerSummary } from '../api/payersApi';
import fieldsStyles from '../components/PayerDirectoryFields.module.css';

function CreatePayerModal({
  isOpen,
  onClose,
  onCreated,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (payer: DirectoryPayer) => void;
}) {
  const { createPayer } = useAllPayers();
  const [values, setValues] = useState<PayerDirectoryFieldValues>(
    DEFAULT_PAYER_DIRECTORY_FIELD_VALUES,
  );

  function handleClose() {
    setValues(DEFAULT_PAYER_DIRECTORY_FIELD_VALUES);
    onClose();
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add payer">
      <div className={fieldsStyles.directoryGrid}>
        <PayerDirectoryFields
          values={values}
          onChange={(patch) => setValues((v) => ({ ...v, ...patch }))}
        />
        <div className={fieldsStyles.saveRow}>
          <Button
            size="sm"
            variant="secondary"
            onClick={handleClose}
            disabled={createPayer.isPending}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() =>
              createPayer.mutate(
                {
                  name: values.name.trim(),
                  aliases: values.aliases
                    .split(',')
                    .map((a) => a.trim())
                    .filter(Boolean),
                  stateEntity: values.stateEntity || null,
                  payerType: values.payerType as 'commercial' | 'medicaid' | 'fedvip',
                  providerLine: values.providerLine || null,
                  verificationStrategy: values.verificationStrategy as
                    | 'voice'
                    | 'staff'
                    | 'blocked',
                  status: values.status as 'active' | 'pending' | 'flagged' | 'blocked',
                },
                {
                  onSuccess: (payer) => {
                    setValues(DEFAULT_PAYER_DIRECTORY_FIELD_VALUES);
                    onCreated(payer);
                  },
                },
              )
            }
            isLoading={createPayer.isPending}
            disabled={!values.name.trim()}
          >
            Add to directory
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ivrMapPills(payer: DirectoryPayerSummary) {
  if (!payer.activeVersion && !payer.proposedVersion) {
    return <span className={patterns.pillMuted}>NOT CHARTED</span>;
  }
  return (
    <>
      {payer.activeVersion && (
        <span className={patterns.pillSuccess}>v{payer.activeVersion} ACTIVE</span>
      )}{' '}
      {payer.proposedVersion && (
        <span className={patterns.pillWarning}>v{payer.proposedVersion} PROPOSED</span>
      )}
    </>
  );
}

export function AgencyInsurancePayersPage() {
  const navigate = useNavigate();
  const { payers, isLoading } = useAllPayers();
  const [showAdd, setShowAdd] = useState(false);

  const openPayer = (p: DirectoryPayerSummary) => navigate(`/agency/insurance-payers/${p.id}`);

  const columns: Column<DirectoryPayerSummary>[] = [
    { key: 'name', header: 'Payer' },
    { key: 'payerType', header: 'Type' },
    { key: 'providerLine', header: 'Provider line', render: (p) => p.providerLine ?? '—' },
    { key: 'ivrMap', header: 'IVR map', render: ivrMapPills },
    {
      key: 'effectiveStrategy',
      header: 'Strategy',
      render: (p) => strategyBadge(p.effectiveStrategy),
    },
    { key: 'status', header: 'Status', render: (p) => statusBadge(p.status) },
    {
      key: 'open',
      header: '',
      align: 'right',
      render: (p) => (
        <span className={patterns.ctaLink} onClick={() => openPayer(p)}>
          Open →
        </span>
      ),
    },
  ];

  return (
    <div className={patterns.page}>
      <PageHeader
        title="Insurance Payers"
        subtitle="The agency-owned payer directory. Practices search and select from this list on their Payers page — edits here apply everywhere."
        actions={<Button onClick={() => setShowAdd(true)}>+ Add payer</Button>}
      />

      <Table
        columns={columns}
        data={payers}
        isLoading={isLoading}
        emptyMessage="No payers in the directory yet."
        onRowClick={openPayer}
      />
      <p className={patterns.sectionFootnote}>
        Row click opens the payer. Edit and delete live on the payer page — this table stays
        scannable.
      </p>

      <CreatePayerModal
        isOpen={showAdd}
        onClose={() => setShowAdd(false)}
        onCreated={(payer) => {
          setShowAdd(false);
          navigate(`/agency/insurance-payers/${payer.id}`);
        }}
      />
    </div>
  );
}
